import { Check } from "lucide-react";
import React, { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import styles from "./TableCells.module.scss";
import { Avatar, AvatarFallback, AvatarImage } from "../../v2/avatar";
import { Badge } from "../../v2/badge";
import { RelativeTime } from "../../v2/relative-time";

import clientLogo from "@/assets/client-logo.png";
import Icons from "@/components/icons";
import { cn } from "@/lib/utils/cn";
import { getVacancyStatus } from "@/lib/vacancies";
import { ClientType } from "@/types/client/ClientType";
import {
  ClientJobBoardType,
  JobBoard2Type,
} from "@/types/jobboards/ClientJobBoardType";
import { SegmentType } from "@/types/segment/segmentType";
import { VacancyType } from "@/types/vacancy/VacancyType";

interface DateTimeCellProps {
  timestamp?: string | null;
  /** When true, tooltip shows date only (no time). Default false. */
  tooltipDateOnly?: boolean;
}

// Vacancy table

interface VacancyTableCellProps {
  vacancy: VacancyType;
}

interface TitleCellProps {
  title: string;
  statusClass?: string;
}

export const TitleCell: React.FC<TitleCellProps> = ({ title, statusClass }) => {
  return (
    <div className={styles?.flexRowTableCell}>
      <Icons.BadgeDotIcon
        fontSize={8}
        className={`${styles.vacancyPublished} ${statusClass || ""}`}
      />
      <span className={cn("text-sm font-semibold", styles?.strong)}>
        {title}
      </span>
    </div>
  );
};

export const DateTimeCell: React.FC<DateTimeCellProps> = ({
  timestamp,
  tooltipDateOnly = false,
}) => {
  if (!timestamp || Number.isNaN(new Date(timestamp).getTime())) {
    return <div>-</div>;
  }

  return (
    <div className="flex flex-row items-center gap-2">
      <RelativeTime date={timestamp} tooltipDateOnly={tooltipDateOnly} />
    </div>
  );
};

export const DateCell: React.FC<DateTimeCellProps> = ({ timestamp }) => {
  if (!timestamp || Number.isNaN(new Date(timestamp).getTime())) {
    return <div>-</div>;
  }
  const [date] = new Date(timestamp)
    .toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "medium" })
    .split(", ");
  return (
    <div className={styles.multiLineTableCell}>
      <span>{date}</span>
    </div>
  );
};

export const VacancyTableTitleCell: React.FC<VacancyTableCellProps> = ({
  vacancy,
}) => {
  const statusClass = React.useMemo(() => {
    const status = getVacancyStatus(vacancy.actual, vacancy.expires);
    if (status === "unpublished") {
      return styles.vacancyNotPublished;
    }
    if (status === "expired") {
      return styles.vacancyExpired;
    }
    return styles.vacancyPublished;
  }, [vacancy.actual, vacancy.expires]);

  return <TitleCell title={vacancy.title} statusClass={statusClass} />;
};

export const VacancyTableCountryCell: React.FC<VacancyTableCellProps> = ({
  vacancy,
}) => (
  <span className="font-mono text-sm font-normal">
    {vacancy.vacancyLoc?.country}
  </span>
);

export const VacancyTableCityCell: React.FC<VacancyTableCellProps> = ({
  vacancy,
}) => (
  <span className="font-mono text-sm font-normal">
    {vacancy.vacancyLoc?.city}
  </span>
);

// Client Table
export const ClientTableNameCell: React.FC<ClientType> = ({
  imagePath,
  name,
}) => {
  return (
    <span className={styles.clientinfo}>
      <Avatar>
        <AvatarImage src={imagePath || clientLogo} alt={name} />
        <AvatarFallback>{name ? name.charAt(0) : "C"}</AvatarFallback>
      </Avatar>
      {name}
    </span>
  );
};

export const SegmentTableStatusCell: React.FC<SegmentType> = ({ status }) => {
  return (
    <Badge variant={"info"} size={"small"}>
      {status}
    </Badge>
  );
};

export const JobBoardsNameCell: React.FC<JobBoard2Type> = ({ name }) => {
  return <TitleCell title={name} />;
};

export const JobBoardsDirectApplyCell: React.FC<ClientJobBoardType> = ({
  directApply = true,
}) => {
  return (
    <>
      {directApply && (
        <div className={styles.iconCell}>
          <Check className="size-4" />
        </div>
      )}
    </>
  );
};

interface TableLinkCellProps {
  link: string;
  label: string;
  target?: React.HTMLAttributeAnchorTarget | undefined;
}
export const TableLinkCell: React.FC<TableLinkCellProps> = ({
  link,
  label,
  target,
}) => {
  const { t } = useTranslation();
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
  };
  return (
    <Link
      to={link}
      target={target}
      className={`${styles.flexRowTableCell} ${styles.link} `}
      onClick={handleClick}
    >
      <span>{t(label)}</span>
    </Link>
  );
};
